import React, { useState } from 'react';
import { 
  Button, 
  Box, 
  VStack, 
  Text, 
  useToast, 
  Code,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { httpsCallable } from 'firebase/functions';
import { functionsUsEast1 } from '../../../utils/init-firebase';

/**
 * Temporary component for generating test data
 * Remove this after testing is complete
 */
export function TestDataGenerator() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [actionType, setActionType] = useState<'generate' | 'delete' | null>(null);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  const seedTestData = httpsCallable(functionsUsEast1, 'seedTestData');
  const deleteTestData = httpsCallable(functionsUsEast1, 'deleteTestData');

  const handleDryRun = async () => {
    setLoading(true);
    setResult(null);
    try {
      const response = await seedTestData({ dryRun: true });
      setResult(response.data);
      toast({
        title: 'Dry run completed',
        description: 'Check the result below',
        status: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to run dry run',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateClick = () => {
    setActionType('generate');
    onOpen();
  };

  const handleDeleteClick = () => {
    setActionType('delete');
    onOpen();
  };

  const handleConfirm = async () => {
    onClose();
    
    if (actionType === 'generate') {
      setLoading(true);
      setResult(null);
      try {
        const response = await seedTestData({ dryRun: false, force: false });
        setResult(response.data);
        toast({
          title: 'Test data generated',
          description: 'Check the result below',
          status: 'success',
          duration: 5000,
        });
      } catch (error: any) {
        console.error('Error:', error);
        toast({
          title: 'Error',
          description: error.message || 'Failed to generate test data',
          status: 'error',
          duration: 5000,
        });
      } finally {
        setLoading(false);
      }
    } else if (actionType === 'delete') {
      setLoading(true);
      setResult(null);
      try {
        const response = await deleteTestData({});
        setResult(response.data);
        toast({
          title: 'Test data deleted',
          description: 'All test data has been removed',
          status: 'success',
          duration: 5000,
        });
      } catch (error: any) {
        console.error('Error:', error);
        toast({
          title: 'Error',
          description: error.message || 'Failed to delete test data',
          status: 'error',
          duration: 5000,
        });
      } finally {
        setLoading(false);
      }
    }
    setActionType(null);
  };

  return (
    <Box p={6}>
      <VStack spacing={4} align="stretch">
        <Text fontSize="xl" fontWeight="bold">
          Test Data Generator
        </Text>
        <Text fontSize="sm" color="gray.600">
          Generate or delete test data for EOS collections. Only available to SuperAdmins.
        </Text>

        <VStack spacing={2} align="stretch">
          <Button
            colorScheme="blue"
            onClick={handleDryRun}
            isLoading={loading}
            loadingText="Running..."
          >
            Dry Run (Validate)
          </Button>

          <Button
            colorScheme="green"
            onClick={handleGenerateClick}
            isLoading={loading}
            loadingText="Generating..."
          >
            Generate Test Data
          </Button>

          <Button
            colorScheme="red"
            onClick={handleDeleteClick}
            isLoading={loading}
            loadingText="Deleting..."
          >
            Delete All Test Data
          </Button>
        </VStack>

        {result && (
          <Box mt={4} p={4} bg="gray.50" borderRadius="md">
            <Text fontWeight="bold" mb={2}>Result:</Text>
            <Code as="pre" p={4} whiteSpace="pre-wrap" fontSize="xs">
              {JSON.stringify(result, null, 2)}
            </Code>
          </Box>
        )}
      </VStack>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {actionType === 'generate' ? 'Generate Test Data' : 'Delete All Test Data'}
            </AlertDialogHeader>
            <AlertDialogBody>
              {actionType === 'generate' 
                ? 'This will generate test data for all EOS collections. Continue?'
                : 'This will delete ALL test data (documents with _testData: true). This action cannot be undone. Are you sure?'}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme={actionType === 'delete' ? 'red' : 'green'}
                onClick={handleConfirm}
                ml={3}
              >
                Confirm
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

